import type * as CC from 'vanilla-cookieconsent';
import { escapeHtml, safeUrl } from '../html';
import type { CookieRows } from '../cookie-table';
import type { BuiltInLanguage, CategoryName, OptionalCategory } from '../types';
import { de } from './de';
import { en } from './en';
import { hr } from './hr';
import { it } from './it';
import { sl } from './sl';
import type { LanguageTexts } from './types';

export type { LanguageTexts, CategoryTexts } from './types';

export const BUILT_IN_TEXTS: Record<BuiltInLanguage, LanguageTexts> = { sl, en, de, it, hr };

export function isBuiltInLanguage(code: string): code is BuiltInLanguage {
  return Object.prototype.hasOwnProperty.call(BUILT_IN_TEXTS, code);
}

/** Texts for a language; unknown languages fall back to English until the site supplies its own. */
export function textsFor(code: string): LanguageTexts {
  return isBuiltInLanguage(code) ? BUILT_IN_TEXTS[code] : en;
}

export interface BuildContext {
  controllerName: string;
  policyUrl: string;
  categories: OptionalCategory[];
  branding: boolean;
  /** Cookie rows per category; adds a cookie table to each section of the settings layer. */
  cookieRows?: CookieRows;
}

/** "a", "a in b", "a, b in c" */
export function joinPurposes(items: string[], and: string): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

/** Credit link. Switch to https://kockica.datavallis.com/ once that subdomain is live (HANDOFF owner action). */
export const CREDIT_URL = 'https://datavallis.com/?utm_source=kockica&utm_medium=banner';

export function brandingHtml(texts: LanguageTexts): string {
  return `<span class="kockica-credit">${escapeHtml(texts.poweredBy)} <a href="${CREDIT_URL}" target="_blank" rel="nofollow noopener">Kockica</a> · Data Vallis</span>`;
}

/** Turn plain language texts into a vanilla-cookieconsent translation. */
export function buildTranslation(texts: LanguageTexts, ctx: BuildContext): CC.Translation {
  const controller = escapeHtml(ctx.controllerName);
  const policyHref = safeUrl(ctx.policyUrl);
  const policyLink = `<a href="${policyHref}">${escapeHtml(texts.policyLinkText)}</a>`;
  const controllerLine = escapeHtml(texts.controllerLine).replace('{controller}', controller);

  const purposes = joinPurposes(
    ctx.categories.map((c) => texts.categories[c].purpose),
    texts.and,
  );
  const lead =
    ctx.categories.length > 0
      ? escapeHtml(texts.consentDescription).replace('{purposes}', escapeHtml(purposes))
      : escapeHtml(texts.consentDescriptionNecessaryOnly);

  const footerParts = [`<a href="${policyHref}">${escapeHtml(texts.policyTitle)}</a>`];
  if (ctx.branding) footerParts.push(brandingHtml(texts));

  const table = (category: CategoryName): { cookieTable: CC.CookieTable } | Record<string, never> => {
    const rows = ctx.cookieRows?.[category];
    if (!rows?.length) return {};
    return {
      cookieTable: {
        headers: { name: texts.tableName, provider: texts.tableProvider, purpose: texts.tablePurpose, duration: texts.tableDuration },
        body: rows.map((r) => ({
          name: escapeHtml(r.name),
          provider: escapeHtml(r.provider),
          purpose: escapeHtml(r.purpose),
          duration: escapeHtml(r.duration),
        })),
      },
    };
  };

  const sections: CC.Section[] = [
    { title: texts.introTitle, description: escapeHtml(texts.introDescription) },
    {
      title: texts.necessaryTitle,
      description: escapeHtml(texts.necessaryDescription),
      linkedCategory: 'necessary',
      ...table('necessary'),
    },
    ...ctx.categories.map((c) => ({
      title: texts.categories[c].title,
      description: escapeHtml(texts.categories[c].description),
      linkedCategory: c,
      ...table(c),
    })),
    {
      title: texts.moreTitle,
      description: `${escapeHtml(texts.moreDescription).replace('{policyLink}', policyLink)} ${controllerLine}`,
    },
  ];

  return {
    consentModal: {
      title: texts.consentTitle,
      description: `${lead} ${controllerLine}`,
      acceptAllBtn: texts.acceptAll,
      acceptNecessaryBtn: texts.rejectAll,
      showPreferencesBtn: texts.preferences,
      footer: footerParts.join(''),
    },
    preferencesModal: {
      title: texts.preferencesTitle,
      acceptAllBtn: texts.acceptAll,
      acceptNecessaryBtn: texts.rejectAll,
      savePreferencesBtn: texts.save,
      closeIconLabel: texts.close,
      serviceCounterLabel: texts.serviceCounter,
      sections,
    },
  };
}
